import { useEffect, useRef } from 'react';
import { supabase } from '../../supabase';
import { AUDIT_LOGIN_KEY } from '../../utils/auth/clearPerUserDeviceState';
import useAssuranceSettled from './useAssuranceSettled';
import { runAfterAuthHandoff } from '../../ui/splash/authHandoffHold';

// RD-BILL-007 — login_success audit dedupe window (5 min). AUDIT_LOGIN_KEY
// lives in clearPerUserDeviceState.js, which also clears it on sign-out.
const AUDIT_LOGIN_WINDOW_MS = 5 * 60 * 1000;

/**
 * login_success audit replaced with profiles.last_login_at update.
 * Reasons for the swap:
 *   • login_success was the highest-volume / lowest-value audit event:
 *     every TOKEN_REFRESHED, every tab open, every multi-device session
 *     generated rows whose only forensic value was "this user has been
 *     active" — answerable with a single timestamp column.
 *   • Brute-force / suspicious-activity detection still works via
 *     auth:login_failed (kept), so security signal isn't lost.
 *   • IO budget: dropping login_success cuts ~30% of audit volume.
 *
 * 5-min localStorage dedupe stays — it now bounds how often we update
 * last_login_at across rapid re-renders and TOKEN_REFRESHED events.
 * One UPDATE per 5min per user instead of per-event.
 *
 * RD-SEC-051 / KAN-676: no write (and no dedupe marker) until the user's
 * assurance is settled — a 2FA-enrolled user at aal1 stamps only after 2FA.
 */
export default function useLastLoginStamp({ sessionUser, tenantId, tenantLoading, loading }) {
  /** Last user id we stamped, so multiple SIGNED_IN events for one session don't double-write. */
  const lastLoggedAuthUserIdRef = useRef(null);
  // `aal` in the deps: a forced-enrollment user is settled at aal1 but the DB refuses
  // the write until aal2 (KAN-708) — the aal change re-runs the stamp.
  const { settled, aal } = useAssuranceSettled(sessionUser?.id);

  useEffect(() => {
    if (!sessionUser?.id) {
      lastLoggedAuthUserIdRef.current = null; // sign-out: next sign-in stamps again
      return;
    }
    if (!tenantId || tenantLoading || loading) return;
    if (!settled) return; // before the marker/ref, or the dedupe swallows the post-2FA stamp
    if (lastLoggedAuthUserIdRef.current === sessionUser.id) return undefined;
    // Non-essential write: waits out a sign-in handoff (authHandoffHold.js).
    return runAfterAuthHandoff(() => {
      lastLoggedAuthUserIdRef.current = sessionUser.id;

      let marker = null;
      try {
        marker = JSON.parse(window.localStorage.getItem(AUDIT_LOGIN_KEY) || 'null');
      } catch { /* corrupt storage — treat as no marker */ }
      const now = Date.now();
      const sameUserRecent =
        marker && marker.userId === sessionUser.id && (now - (marker.at || 0)) < AUDIT_LOGIN_WINDOW_MS;
      if (sameUserRecent) return;

      // Fire-and-forget profile timestamp update. RLS (profiles_update_own_locale)
      // needs the 2FA check, so a write the server refused updates 0 rows with no
      // error (KAN-708): the dedupe marker is written ONLY after a row was updated,
      // otherwise it would swallow the post-enrollment stamp. A refused/failed
      // write also clears the per-session ref so the next settle retries.
      const userId = sessionUser.id;
      void supabase
        .from('profiles')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', userId)
        .select('id')
        .then(({ data, error } = {}) => {
          if (error || !Array.isArray(data) || data.length === 0) {
            if (lastLoggedAuthUserIdRef.current === userId) lastLoggedAuthUserIdRef.current = null;
            return;
          }
          try {
            window.localStorage.setItem(AUDIT_LOGIN_KEY, JSON.stringify({ userId, at: now }));
          } catch { /* storage full / disabled — best effort */ }
        }, () => { /* fail-soft — non-critical timestamp */ });
    });
  }, [sessionUser?.id, tenantId, tenantLoading, loading, settled, aal]);
}
